/**
 * Carousel Skeleton Loader
 *
 * Dynamically injects and removes placeholder skeleton cards in carousel sections
 * while content is being fetched. Provides helper functions to manually control
 * skeleton rendering.
 *
 * Behavior:
 * - Intended for use with Shopify carousel sections (e.g., featured collections, recommendations).
 * - Skips injection if real slides are already present.
 * - Appends a configurable number of skeleton cards (default: 6).
 * - Marks skeleton containers with `data-carousel-skeleton` for easy removal.
 *
 * Usage:
 * - Call `window.addCarouselSkeleton(section)` when starting to fetch carousel data.
 * - Call `window.removeCarouselSkeleton(section)` after the content has loaded.
 *
 * Example:
 *
 * ```js
 * '/sections/featured-collection.liquid'
 *
 * const featuredCollection = document.querySelector('#fp-featured-collection');
 *
 * // Show skeleton while fetching
 * window.addCarouselSkeleton(featuredCollection);
 *
 * fetch('/path/to/products')
 *   .then(res => res.text())
 *   .then(html => {
 *     featuredCollection.innerHTML = html;
 *     window.removeCarouselSkeleton(featuredCollection);
 *   });
 * ```
 *
 * NOTE: Currently this is already added globally on the theme.liquid file.
 *
 * Usage:
 * Include this script with `defer` so the DOM is available before execution:
 *
 * <script src="{{ 'product-carousel-skeleton.js' | asset_url }}" defer="defer"></script>
 */
(function () {
  const MAX_VISIBLE = 6;

  const createCard = () => {
    const card = document.createElement("li");
    card.className = "fp-carousel-skeleton__item";
    card.innerHTML = `
      <div class="fp-plp-skeleton__card-wrapper">
        <div class="fp-carousel-skeleton__product-card">
          <div class="fp-carousel-skeleton__product-card-image-wrapper">
            <div class="fp-carousel-skeleton__product-card-image-dummy"></div>
            <div class="fp-carousel-skeleton__product-card-image">&nbsp;</div>
          </div>
          <div class="fp-carousel-skeleton__product-card-vendor"></div>
          <div class="fp-carousel-skeleton__product-card-name"></div>
          <div class="fp-carousel-skeleton__product-card-condition"></div>
          <div class="fp-carousel-skeleton__product-card-price"></div>
        </div>
      </div>
    `;
    return card;
  };

  /**
   * Removes a carousel skeleton loader from a given section.
   *
   * @param {HTMLElement} section - The carousel section element to clean up.
   */
  window.removeCarouselSkeleton = function (section) {
    if (!section) return;

    const skeleton = section.querySelector("[data-carousel-skeleton=true]");
    if (skeleton) {
      skeleton.remove();
    }
  };

  /**
   * Injects a skeleton loader into a given carousel section.
   *
   * @param {HTMLElement} section - The carousel section element to populate with skeleton cards.
   * @param {number} [maxVisible=6] - Number of placeholder cards to display.
   *
   * @example
   * const section = document.querySelector('#fp-featured-collection');
   * window.addCarouselSkeleton(section, 4);
   */
  window.addCarouselSkeleton = function (section, maxVisible = MAX_VISIBLE) {
    if (!section) return;

    const existingSlides = section.querySelectorAll("[id^=Slide-template--]");
    if (existingSlides.length) return;

    const wrapper = document.createElement("div");
    wrapper.setAttribute("data-carousel-skeleton", "true");
    wrapper.setAttribute("aria-hidden", "true");

    const list = document.createElement("ol");
    list.className = "fp-carousel-skeleton__list";

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < maxVisible; i++) {
      fragment.appendChild(createCard());
    }

    list.appendChild(fragment);
    wrapper.appendChild(list);
    section.appendChild(wrapper);
  };
})();
